import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { scrollToSection } from "@/lib/scroll";
import { SUMMIT } from "@/constants/summit";

const NAV_LINKS = [
  { label: "Overview", id: "overview", testId: "nav-link-overview" },
  { label: "About the Summit", route: "/about", testId: "nav-link-about" },
  { label: "Who Attends", id: "audience", testId: "nav-link-audience" },
  { label: "Agenda", id: "agenda", testId: "nav-link-schedule" },
];

const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const goTo = (id) => {
    setOpen(false);
    if (location.pathname !== "/") {
      navigate("/", { state: { scrollTo: id } });
    } else {
      scrollToSection(id);
    }
  };

  return (
    <header
      data-testid="main-navigation"
      className={`fixed top-0 inset-x-0 z-50 transition-[background-color,border-color] duration-500 border-b ${
        scrolled || open ? "glass border-white/10" : "bg-transparent border-transparent"
      }`}
    >
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="flex h-[72px] items-center justify-between">
          <Link to="/" data-testid="nav-brand-logo" className="flex items-center gap-3 group" onClick={() => setOpen(false)}>
            <img
              src="/assets/brand-logo.png"
              alt="The Choice summit icon"
              className="h-10 w-10 object-contain drop-shadow-[0_0_14px_rgba(255,87,34,0.45)] transition-transform duration-300 group-hover:rotate-6"
            />
            <span className="leading-none">
              <span className="block font-display text-base font-bold tracking-[0.18em]">{SUMMIT.name}</span>
              <span className="block font-mono text-[9px] sm:text-[10px] uppercase tracking-[0.16em] text-white/50">
                Presented by The Edutainment Box & DABO Detroit
              </span>
            </span>
          </Link>

          <nav className="hidden lg:flex items-center gap-8">
            {NAV_LINKS.map((l) =>
              l.route ? (
                <Link
                  key={l.testId}
                  to={l.route}
                  data-testid={l.testId}
                  className={`font-mono text-xs uppercase tracking-[0.18em] transition-colors duration-300 hover:text-brand-400 ${
                    location.pathname === l.route ? "text-brand-400" : "text-white/70"
                  }`}
                >
                  {l.label}
                </Link>
              ) : (
                <button
                  key={l.testId}
                  data-testid={l.testId}
                  onClick={() => goTo(l.id)}
                  className="font-mono text-xs uppercase tracking-[0.18em] text-white/70 transition-colors duration-300 hover:text-brand-400"
                >
                  {l.label}
                </button>
              )
            )}
            <Link
              to="/speakers"
              data-testid="nav-link-speakers"
              className={`font-mono text-xs uppercase tracking-[0.18em] transition-colors duration-300 hover:text-brand-400 ${
                location.pathname === "/speakers" ? "text-brand-400" : "text-white/70"
              }`}
            >
              Speakers
            </Link>
          </nav>

          <div className="flex items-center gap-3">
            <Link
              to="/confirm-attendance"
              data-testid="nav-confirm-attendance-button"
              className="hidden sm:inline-flex items-center gap-2 rounded-full bg-brand-500 px-5 py-2.5 font-display text-sm font-semibold text-white transition-all duration-300 hover:bg-brand-600 hover:shadow-[0_0_32px_rgba(255,87,34,0.5)]"
            >
              Confirm Attendance
              <ArrowUpRight className="h-4 w-4" />
            </Link>
            <button
              data-testid="nav-mobile-menu-button"
              onClick={() => setOpen(!open)}
              className="lg:hidden grid h-10 w-10 place-items-center rounded-md border border-white/15 text-white"
              aria-label="Toggle menu"
            >
              {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="lg:hidden glass border-t border-white/10 overflow-hidden"
            data-testid="nav-mobile-menu"
          >
            <div className="px-6 py-6 flex flex-col gap-5">
              {NAV_LINKS.map((l) =>
                l.route ? (
                  <Link
                    key={l.testId}
                    to={l.route}
                    data-testid={`mobile-${l.testId}`}
                    onClick={() => setOpen(false)}
                    className="font-display text-2xl font-semibold text-white/85 hover:text-brand-400 transition-colors"
                  >
                    {l.label}
                  </Link>
                ) : (
                  <button
                    key={l.testId}
                    data-testid={`mobile-${l.testId}`}
                    onClick={() => goTo(l.id)}
                    className="text-left font-display text-2xl font-semibold text-white/85 hover:text-brand-400 transition-colors"
                  >
                    {l.label}
                  </button>
                )
              )}
              <Link
                to="/speakers"
                data-testid="mobile-nav-link-speakers"
                onClick={() => setOpen(false)}
                className="font-display text-2xl font-semibold text-white/85 hover:text-brand-400 transition-colors"
              >
                Speakers
              </Link>
              <Link
                to="/confirm-attendance"
                data-testid="mobile-nav-confirm-attendance-button"
                onClick={() => setOpen(false)}
                className="mt-2 inline-flex w-fit items-center gap-2 rounded-full bg-brand-500 px-6 py-3 font-display text-sm font-semibold"
              >
                Confirm Attendance <ArrowUpRight className="h-4 w-4" />
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Navbar;
